/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID } from '@gh/common-shared-data';
import { CollectionComparator, EMPTY_ARRAY, sortedIndexWith } from '@gh/core-util';
import { TableIndex, TableIndexBuilder, TableOrderIndex, TableOrderIndexDriver } from './table.interfaces';

export function createOrderIndexDriver<T>(name: string,
                                          getId: TransformFn<T, ID>,
                                          includes: PredicateFn<T>,
                                          comparator: CollectionComparator<T>): TableOrderIndexDriver<T> {
  const create = () => EMPTY_ARRAY;
  const modify = (index: TableOrderIndex<T>) => new TableOrderIndexBuilder(index);
  const getOrderedData = (index: TableOrderIndex<T>) => index || EMPTY_ARRAY;

  class TableOrderIndexBuilder implements TableIndexBuilder<T> {
    private copied = false;

    constructor(private index: T[]) {

    }

    insert(row: T): void {
      if (!includes(row)) {
        return;
      }

      this.copy();

      const { index } = this;
      const orderIndex = sortedIndexWith(index, row, comparator);

      index.splice(orderIndex, 0, row);
    }

    update(oldRow: T, newRow: T): void {
      this.remove(oldRow);
      this.insert(newRow);
    }

    remove(row: T): void {
      if (!includes(row)) {
        return;
      }

      this.copy();

      const { index } = this;
      const orderIndex = sortedIndexWith(index, row, comparator);

      index.splice(orderIndex, 1);
    }

    build(): TableIndex<T> {
      return this.index;
    }

    private copy(): void {
      if (!this.copied) {
        this.index = this.index.concat();
        this.copied = true;
      }
    }
  }

  return { name, create, modify, getOrderedData };
}
